import { cloneDeep } from 'lodash'
import { generatePartialUserData, asyncGeneratePartialUserData } from './partialUserData'
import { passesConditions } from './conditions'
import type { Component, Flow, FlowPage, Context } from '../models/types'

//* Non-async version for client-side
export function stripVariantConditions({
  context,
  baseFlow,
  baseUserData,
  searchParams,
}: {
  context: Context
  baseFlow: Flow
  baseUserData?: { [key: string]: string }
  searchParams: URLSearchParams
}): {
  flow: Flow
  data: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
} {
  if (context.isServerSide) {
    throw new Error('Use asyncStripVariantConditions for server-side calls')
  }

  const flow = cloneDeep(baseFlow)

  //* Initialize user data
  const { userData, userDataWithoutDefaults } = generatePartialUserData({
    context,
    flow,
    providedUserData: baseUserData,
    searchParams,
  })

  return processFlowWithUserData(flow, userData, userDataWithoutDefaults)
}

//* Async version for server-side
export async function asyncStripVariantConditions({
  context,
  baseFlow,
  baseUserData,
  projectId,
  searchParams,
}: {
  context: Context
  baseFlow: Flow
  baseUserData?: { [key: string]: string }
  projectId: string
  searchParams: URLSearchParams
}): Promise<{
  flow: Flow
  data: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
}> {
  const flow = cloneDeep(baseFlow)

  //* Initialize user data
  const { userData, userDataWithoutDefaults } = await asyncGeneratePartialUserData({
    context,
    flow,
    providedUserData: baseUserData,
    projectId,
    searchParams,
  })

  return processFlowWithUserData(flow, userData, userDataWithoutDefaults)
}

//* Shared code to process the flow after we have user data
function processFlowWithUserData(
  flow: Flow,
  userData: { [key: string]: string },
  userDataWithoutDefaults: { [key: string]: string }
): {
  flow: Flow
  data: { [key: string]: string }
  userDataWithoutDefaults: { [key: string]: string }
} {
  //* Create the component condition reducer for setting __hide_initially true if the component does not pass conditions
  const componentConditionReducer = (acc: Component[], c: Component) => {
    if (Array.isArray(c.conditions) && c.conditions.length > 0) {
      const valid = passesConditions(c.conditions, userData)
      if (!valid) {
        c.__hide_initially = true
      }
    }
    acc.push(c)
    return acc
  }

  //* Pass the received flow json global components through the component condition reducer
  if (Array.isArray(flow.components) && flow.components.length > 0) {
    //* Update the global components in the json
    flow.components = flow.components.reduce(componentConditionReducer, [])
  }

  //* Pass the received flow json pages through the page condition reducer for setting __hide_initially true if the page does not pass condtions
  const allowedPages = (flow.pages || []).reduce((acc: FlowPage[], page) => {
    if (Array.isArray(page.conditions) && page.conditions.length > 0) {
      const valid = passesConditions(page.conditions, userData)
      if (!valid) page.__hide_initially = true
    }

    //* Pass the received flow json page components through the component condition reducer
    page.components = page.components.reduce(componentConditionReducer, [])

    acc.push(page)
    return acc
  }, [])

  //* Update the pages in the json
  flow.pages = allowedPages

  return { flow, data: userData, userDataWithoutDefaults }
}
